import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import { useRescueDrill } from '@/hooks/use-safety';
import { formatDateOnly } from '@/lib/date-utils';
import type { DrillOutcome } from '@/lib/types';

const outcomeColors: Record<DrillOutcome, string> = {
  successful: 'bg-green-100 text-green-800',
  partial: 'bg-yellow-100 text-yellow-800',
  failed: 'bg-red-100 text-red-800',
};

export function RescueDrillDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const drillId = parseInt(id || '0', 10);
  const { data: drill, isLoading } = useRescueDrill(drillId);

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!drill) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-600">Rescue drill not found.</p>
        <Button asChild className="mt-4">
          <Link to="/safety/rescue-drills">Back to Rescue Drills</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/safety/rescue-drills')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Rescue Drill — {formatDateOnly(drill.date)}
            </h1>
            <p className="text-gray-600">{drill.location}</p>
          </div>
        </div>
        <Badge className={outcomeColors[drill.outcome]}>
          {drill.outcome}
        </Badge>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Drill Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Date:</span>{' '}
              {formatDateOnly(drill.date)}
            </div>
            <div>
              <span className="text-gray-500">Location:</span> {drill.location}
            </div>
            <div>
              <span className="text-gray-500">Duration:</span>{' '}
              {drill.duration_minutes ? `${drill.duration_minutes} minutes` : 'Not recorded'}
            </div>
            <div>
              <span className="text-gray-500">Conducted By:</span> {drill.created_by_username}
            </div>
            {drill.rescue_plan_title && (
              <div>
                <span className="text-gray-500">Rescue Plan:</span> {drill.rescue_plan_title}
              </div>
            )}
            {drill.next_drill_due && (
              <div>
                <span className="text-gray-500">Next Drill Due:</span>{' '}
                {formatDateOnly(drill.next_drill_due)}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Scenario</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm whitespace-pre-wrap">{drill.scenario_description}</p>
        </CardContent>
      </Card>

      {drill.participants && (
        <Card>
          <CardHeader>
            <CardTitle>Participants</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{drill.participants}</p>
          </CardContent>
        </Card>
      )}

      {drill.lessons_learned && (
        <Card>
          <CardHeader>
            <CardTitle>Lessons Learned</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{drill.lessons_learned}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
